import type {
  EventTrackingValues,
  InteractionType,
  SwipeDirection
} from '@carvana/ds-carousel';
import { type RefObject, useCallback, useEffect, useRef } from 'react';

type TrackingState = Omit<EventTrackingValues, 'direction' | 'interactionType'>;

type OwnProps = {
  nextDisabled: boolean;
  prevDisabled: boolean;
  slidesRef: RefObject<HTMLDivElement | null>;
  trackEvent?: (values: EventTrackingValues) => void;
  trackingRef: RefObject<TrackingState>;
};

const MIN_SWIPE_DISTANCE = 50;
const WHEEL_DEBOUNCE_MS = 200;

export default function useCarouselTracking({
  nextDisabled,
  prevDisabled,
  slidesRef,
  trackEvent,
  trackingRef
}: OwnProps) {
  const touchEndXRef = useRef<number | null>(null);
  const touchStartXRef = useRef<number | null>(null);

  const trackDirectionEvent = useCallback(
    (direction: SwipeDirection, interactionType: InteractionType) => {
      if (trackEvent && trackingRef.current) {
        trackEvent({
          ...trackingRef.current,
          direction,
          interactionType,
          userInteracted: true
        });
      }
    },
    [trackEvent, trackingRef]
  );

  const handleSwipe = useCallback(() => {
    if (touchStartXRef.current === null || touchEndXRef.current === null)
      return;

    const swipeDistance = touchEndXRef.current - touchStartXRef.current;
    const isSignificantSwipe = Math.abs(swipeDistance) > MIN_SWIPE_DISTANCE;

    if (isSignificantSwipe) {
      if (swipeDistance > 0) {
        if (!prevDisabled) trackDirectionEvent('prev', 'swipe');
      } else if (!nextDisabled) {
        trackDirectionEvent('next', 'swipe');
      }
    }

    touchEndXRef.current = null;
    touchStartXRef.current = null;
  }, [nextDisabled, prevDisabled, trackDirectionEvent]);

  useEffect(() => {
    const currentSlidesRef = slidesRef.current;
    if (!currentSlidesRef || !trackEvent) return;

    const handleTouchStart = (event: TouchEvent) => {
      touchStartXRef.current = event.touches[0].clientX;
      touchEndXRef.current = null;
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (touchStartXRef.current === null) return;
      touchEndXRef.current = event.changedTouches[0].clientX;
      handleSwipe();
    };

    currentSlidesRef.addEventListener('touchstart', handleTouchStart, {
      passive: true
    });
    currentSlidesRef.addEventListener('touchend', handleTouchEnd, {
      passive: true
    });

    return () => {
      currentSlidesRef.removeEventListener('touchstart', handleTouchStart);
      currentSlidesRef.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleSwipe, slidesRef, trackEvent]);

  useEffect(() => {
    const currentSlidesRef = slidesRef.current;
    if (!currentSlidesRef || !trackEvent) return;

    let debounceTimer: number | undefined;
    let lastWheelDirection: SwipeDirection | null = null;
    let wheelSessionActive = false;

    // Fires once per wheel session, after the user stops scrolling.
    const handleWheel = (event: WheelEvent) => {
      // Only horizontal wheel movement scrolls the slides; vertical wheel scrolls the page.
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        lastWheelDirection = event.deltaX > 0 ? 'next' : 'prev';
        wheelSessionActive = true;

        if (debounceTimer) clearTimeout(debounceTimer);

        debounceTimer = window.setTimeout(() => {
          if (lastWheelDirection && wheelSessionActive) {
            const canMove =
              lastWheelDirection === 'next' ? !nextDisabled : !prevDisabled;
            if (canMove) trackDirectionEvent(lastWheelDirection, 'scroll');
            lastWheelDirection = null;
            wheelSessionActive = false;
          }
        }, WHEEL_DEBOUNCE_MS);
      }
    };

    currentSlidesRef.addEventListener('wheel', handleWheel, { passive: true });

    return () => {
      currentSlidesRef.removeEventListener('wheel', handleWheel);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [nextDisabled, prevDisabled, slidesRef, trackDirectionEvent, trackEvent]);

  return { trackDirectionEvent };
}
